/**
 * Amazon Affiliate Integration
 * Compliant with Amazon Associates Program Operating Agreement
 */

// Amazon Associate Tag
const AMAZON_AFFILIATE_TAG = 'earnechorea0f-20';

/**
 * Generate Amazon search URL with affiliate tag
 * @param {string} searchTerm - Product to search for
 * @returns {string} Amazon search URL with affiliate tag
 */
export function getAmazonSearchUrl(searchTerm) {
  const encodedSearch = encodeURIComponent(searchTerm);
  return `https://www.amazon.com/s?k=${encodedSearch}&tag=${AMAZON_AFFILIATE_TAG}`;
}

/**
 * Generate Amazon product URL with affiliate tag
 * @param {string} asin - Amazon Standard Identification Number
 * @returns {string} Amazon product URL with affiliate tag
 */
export function getAmazonProductUrl(asin) {
  return `https://www.amazon.com/dp/${asin}?tag=${AMAZON_AFFILIATE_TAG}`;
}

/**
 * Amazon Associates disclosure text (required by agreement)
 * Must be displayed wherever affiliate links are shown
 */
export const AMAZON_DISCLOSURE = 
  "As an Amazon Associate, Earn-E earns from qualifying purchases.";

/**
 * Check if user can see shopping features
 * Per Amazon agreement: NO advertising/banners for child accounts
 * @param {object} userProfile - User profile object
 * @returns {boolean} True if user can see shopping features
 */
export function canViewShoppingFeatures(userProfile) {
  // Only parents can see Amazon affiliate links
  return userProfile?.role === 'parent';
}

/**
 * Extract product information from Amazon URL
 * This is a basic implementation - in production, you'd use Amazon Product Advertising API
 */
export async function extractAmazonProduct(amazonUrl) {
  try {
    console.log('🛒 Extracting Amazon product from:', amazonUrl);
    
    // Handle Amazon short URLs (a.co/d/...) - these redirect to actual product pages
    if (amazonUrl.includes('a.co/d/')) {
      console.log('📎 Detected Amazon short URL, extracting ASIN from path...');
      // Extract the ASIN-like code from the short URL
      const shortCodeMatch = amazonUrl.match(/a\.co\/d\/([A-Za-z0-9]+)/i);
      if (shortCodeMatch) {
        const shortCode = shortCodeMatch[1];
        console.log('Short code found:', shortCode);
        // Unfortunately we can't expand the short URL without making an API call
        // Return a placeholder that the user can manually fix
        return {
          asin: shortCode,
          name: 'Amazon Product (from short link)',
          price: 29.99,
          imageUrl: '',
          amazonUrl: amazonUrl,
          extracted: true,
          note: 'Short URL detected - please verify product details'
        };
      }
    }
    
    // Basic URL validation - accept all Amazon domains
    if (!amazonUrl.includes('amazon') && !amazonUrl.includes('a.co')) {
      throw new Error('Invalid Amazon URL. Must contain "amazon" or "a.co"');
    }

    // Extract ASIN from URL - multiple patterns for mobile/desktop
    const asinMatch = 
      amazonUrl.match(/\/dp\/([A-Z0-9]{10})/i) ||           // Desktop: /dp/ASIN
      amazonUrl.match(/\/gp\/product\/([A-Z0-9]{10})/i) ||   // Alternate: /gp/product/ASIN
      amazonUrl.match(/\/slredirect\/.*?\/([A-Z0-9]{10})/i) || // Mobile share link
      amazonUrl.match(/(?:ASIN|asin)=([A-Z0-9]{10})/i) ||    // Query param
      amazonUrl.match(/\/exec\/obidos\/ASIN\/([A-Z0-9]{10})/i) || // Old format
      amazonUrl.match(/\/hz\/rd\/(A[Z0-9]{9})/i);            // Mobile app link
    
    if (!asinMatch) {
      console.log('URL patterns tried:');
      console.log('- /dp/ASIN');
      console.log('- /gp/product/ASIN');
      console.log('- /slredirect/ASIN');
      console.log('- ASIN= parameter');
      console.log('URL received:', amazonUrl);
      
      // If it's an a.co link, return it anyway
      if (amazonUrl.includes('a.co')) {
        return {
          asin: 'MANUAL',
          name: 'Amazon Product',
          price: 29.99,
          imageUrl: '',
          amazonUrl: amazonUrl,
          extracted: true,
          note: 'Please verify product details'
        };
      }
      
      throw new Error('Could not find product ID. Try copying the full URL from your browser address bar.');
    }

    const asin = asinMatch[1];
    console.log('📦 Found ASIN:', asin);

    // Extract product name from URL path
    let extractedName = '';
    let extractedPrice = 0;
    
    // Extract name from URL path - everything after /dp/ASIN/
    const urlPath = amazonUrl.split('/dp/')[1] || amazonUrl.split('/gp/product/')[1] || '';
    // Remove ASIN and get product name
    const namePart = urlPath.replace(/^[A-Z0-9]{10}\/?/i, '').split('/')[0];
    if (namePart) {
      extractedName = decodeURIComponent(namePart)
        .replace(/-/g, ' ')
        .replace(/\+/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
    }
    
    // Smart price estimation based on product category and URL analysis
    const lowerUrl = amazonUrl.toLowerCase();
    const lowerName = extractedName.toLowerCase();
    
    // Check for GPUs and high-end hardware FIRST (before general categories)
    if (lowerName.includes('rtx 3090') || lowerName.includes('3090 ti') || lowerName.includes('geforce rtx 3090')) {
      extractedPrice = 1449.99; // RTX 3090 GPU
    } else if (lowerName.includes('rtx 4090') || lowerName.includes('4090')) {
      extractedPrice = 1599.99; // RTX 4090
    } else if (lowerName.includes('rtx 3080') || lowerName.includes('3080')) {
      extractedPrice = 699.99; // RTX 3080
    } else if (lowerName.includes('rtx 4080') || lowerName.includes('4080')) {
      extractedPrice = 1199.99; // RTX 4080
    } else if (lowerName.includes('rtx') || lowerName.includes('gpu') || lowerName.includes('graphics card')) {
      extractedPrice = 499.99; // Generic GPU
    } else if (lowerName.includes('laptop') || lowerName.includes('notebook')) {
      if (lowerName.includes('gaming') || lowerName.includes('rog') || lowerName.includes('alienware') || lowerName.includes('razer')) {
        extractedPrice = 1899.99; // Gaming laptop
      } else {
        extractedPrice = 699.99; // Regular laptop
      }
    } else if (lowerName.includes('nintendo') || lowerName.includes('switch')) {
      extractedPrice = 299.99; // Nintendo Switch
    } else if (lowerName.includes('lego')) {
      if (lowerName.includes('star wars') || lowerName.includes('millennium')) {
        extractedPrice = 169.99; // Big LEGO sets
      } else {
        extractedPrice = 49.99; // Regular LEGO
      }
    } else if (lowerName.includes('bike') || lowerName.includes('bicycle')) {
      extractedPrice = 399.99; // Bicycles
    } else if (lowerName.includes('toy') || lowerName.includes('game') || lowerName.includes('action figure')) {
      extractedPrice = 29.99; // Toys/games
    } else if (lowerName.includes('book')) {
      extractedPrice = 19.99; // Books
    } else if (lowerName.includes('headphone') || lowerName.includes('earbud') || lowerName.includes('airpod')) {
      if (lowerName.includes('sony') || lowerName.includes('bose') || lowerName.includes('airpods pro')) {
        extractedPrice = 249.99; // Premium audio
      } else {
        extractedPrice = 79.99; // Regular audio
      }
    } else if (lowerName.includes('iphone') || lowerName.includes('galaxy') || lowerName.includes('pixel')) {
      extractedPrice = 999.99; // Smartphones
    } else if (lowerName.includes('ipad') || lowerName.includes('tablet')) {
      extractedPrice = 499.99; // Tablets
    } else if (lowerName.includes('tv') || lowerName.includes('television')) {
      if (lowerName.includes('65') || lowerName.includes('75') || lowerName.includes('85')) {
        extractedPrice = 899.99; // Large TV
      } else {
        extractedPrice = 399.99; // Regular TV
      }
    } else if (lowerName.includes('camera') || lowerName.includes('dslr') || lowerName.includes('mirrorless')) {
      extractedPrice = 799.99; // Cameras
    } else {
      extractedPrice = 39.99; // Default (lower than before)
    }

    // Try to get better image URL
    const imageUrl = `https://m.media-amazon.com/images/I/${asin}.jpg`;

    const result = {
      asin,
      name: extractedName || 'Amazon Product',
      price: extractedPrice,
      imageUrl,
      amazonUrl: amazonUrl.includes(AMAZON_AFFILIATE_TAG) ? amazonUrl : `${amazonUrl}${amazonUrl.includes('?') ? '&' : '?'}tag=${AMAZON_AFFILIATE_TAG}`,
      extracted: true,
      category: getProductCategory(lowerUrl),
      estimatedPrice: true // Flag to indicate this is estimated
    };
    
    console.log('✅ Extraction result:', result);
    return result;
  } catch (error) {
    console.error('❌ Error extracting Amazon product:', error);
    throw error;
  }
}

/**
 * Add affiliate tag to Amazon URL if not present
 */
export function addAffiliateTag(amazonUrl) {
  if (!amazonUrl || !amazonUrl.includes('amazon.com')) {
    return amazonUrl;
  }

  // Check if tag already exists
  if (amazonUrl.includes(`tag=${AMAZON_AFFILIATE_TAG}`)) {
    return amazonUrl;
  }

  // Add tag
  const separator = amazonUrl.includes('?') ? '&' : '?';
  return `${amazonUrl}${separator}tag=${AMAZON_AFFILIATE_TAG}`;
}

/**
 * Popular categories for quick search
 */
export const POPULAR_CATEGORIES = [
  { name: 'Toys & Games', search: 'toys' },
  { name: 'Books', search: 'books for kids' },
  { name: 'School Supplies', search: 'school supplies' },
  { name: 'Sports & Outdoors', search: 'sports equipment' },
  { name: 'Electronics', search: 'electronics' },
  { name: 'Home & Kitchen', search: 'home kitchen' },
  { name: 'Clothing', search: 'clothing' },
  { name: 'Groceries', search: 'grocery' }
];

/**
 * Get product category from URL
 * @param {string} url - Lowercase URL
 * @returns {string} Product category
 */
function getProductCategory(url) {
  if (url.includes('laptop') || url.includes('gaming')) return 'Electronics';
  if (url.includes('toy') || url.includes('game')) return 'Toys & Games';
  if (url.includes('book')) return 'Books';
  if (url.includes('clothing') || url.includes('shirt')) return 'Clothing';
  if (url.includes('kitchen') || url.includes('cooking')) return 'Kitchen';
  if (url.includes('sports') || url.includes('fitness')) return 'Sports';
  if (url.includes('home') || url.includes('furniture')) return 'Home & Garden';
  return 'General';
}

/**
 * Get suggested products based on shopping list item
 * @param {string} itemName - Shopping list item name
 * @returns {object} Suggestion with Amazon search URL
 */
export function getSuggestedProducts(itemName) {
  return {
    itemName,
    amazonSearchUrl: getAmazonSearchUrl(itemName),
    disclosure: AMAZON_DISCLOSURE
  };
}


